import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAuth, useLanguage, useSelectLanguage } from "@/app";
import { usePlayer } from "@/app/providers/PlayerProvider";
import { sidebarTranslations } from "@/shared/locales";
import { IconBrandTelegram, IconMusic, IconPlayerPlay, IconPlayerPause, IconHeart, IconWallet, IconDoor, IconPlus, IconChartBar, IconHelp, IconGift, } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, ThemeSwitch, LanguageSwitch, Menu } from "@/shared/ui";
import { MaxMessengerBrandIcon } from "@/shared/ui/MaxMessengerBrandIcon/MaxMessengerBrandIcon";
import { openSupportBot } from "@/shared/lib/support/openSupportBot";
import { getMiniAppPlatform } from "@/shared/lib/miniApp";
import styles from "./MiniAppHomePage.module.scss";
import { appConfig } from "@/shared/config";
import { getTelegramWebApp, isTelegramMiniApp } from "@/shared/lib/telegram";
import { isMaxMiniApp } from "@/shared/lib/max";
import { getCharts, getSongHistory } from "@/shared/api/songs";
import { LogoutConfirmModal } from "@/shared/ui/LogoutConfirmModal";
const MiniAppHomePage = ({ openLoginModal, }) => {
    const { language } = useLanguage();
    const handleSelectLanguage = useSelectLanguage();
    const { user, isAuthenticated, isGuest, logout } = useAuth();
    const navigate = useNavigate();
    const t = sidebarTranslations[language];
    const { openPlayer, setPlaylist, currentTrack, isPlaying, togglePlayPause, isPlayerOpen, } = usePlayer();
    const [topTrack, setTopTrack] = useState(null);
    const [topTrackPeriod, setTopTrackPeriod] = useState(null);
    const [isLoadingTopTrack, setIsLoadingTopTrack] = useState(true);
    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(0);
    const [failedImages, setFailedImages] = useState(new Set());
    const [durationLabel, setDurationLabel] = useState("0:00");
    const [logoutConfirmOpened, setLogoutConfirmOpened] = useState(false);
    const [songCount, setSongCount] = useState(null);
    const noTags = language === "ru" ? "Без названия" : "No title";
    const formatDate = (dateString) => {
        if (!dateString)
            return "";
        try {
            return new Date(dateString).toLocaleDateString(language === "ru" ? "ru-RU" : "en-US");
        }
        catch {
            return "";
        }
    };
    const handleTopUp = () => {
        navigate("/balance");
    };
    const handleCreateDemoSong = () => {
        navigate("/generate-song");
    };
    const performLogout = async () => {
        await logout();
    };
    const handleJoinCommunity = () => {
        const webApp = getTelegramWebApp();
        const link = appConfig.socialLinks.telegramCommunity;
        if (webApp) {
            if (webApp.openTelegramLink) {
                try {
                    webApp.openTelegramLink(link);
                    return;
                }
                catch (error) {
                    console.error("Error opening Telegram link:", error);
                }
            }
            if (webApp.openLink) {
                webApp.openLink(link);
                return;
            }
        }
        window.open(link, "_blank");
    };
    useEffect(() => {
        const loadTopTrack = async () => {
            setIsLoadingTopTrack(true);
            try {
                const tryTypes = ["month", "global"];
                for (const type of tryTypes) {
                    const response = await getCharts({ type, limit: 1, offset: 0 });
                    if (response.results.length > 0) {
                        const track = response.results[0];
                        setTopTrack(track);
                        setTopTrackPeriod(type);
                        setIsLiked(track.is_liked);
                        setLikeCount(track.like_count);
                        setIsLoadingTopTrack(false);
                        return;
                    }
                }
            }
            catch (error) {
                console.error("Error loading top track:", error);
            }
            finally {
                setIsLoadingTopTrack(false);
            }
        };
        loadTopTrack();
    }, []);
    useEffect(() => {
        if (!isAuthenticated) {
            setSongCount(null);
            return;
        }
        let cancelled = false;
        const loadSongCount = async () => {
            try {
                const response = await getSongHistory({ limit: 1, offset: 0 });
                if (!cancelled) {
                    setSongCount(response.total ?? 0);
                }
            }
            catch (error) {
                console.error("Error loading song count:", error);
            }
        };
        loadSongCount();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);
    useEffect(() => {
        if (!topTrack) {
            setDurationLabel("0:00");
            return;
        }
        setPlaylist([
            {
                id: topTrack.audio_id,
                title: topTrack.song_title || noTags,
                duration: "0:00",
                date: formatDate(topTrack.song_created_at),
                imageUrl: topTrack.cover_url || undefined,
                audioUrl: topTrack.audio_url,
                audioId: topTrack.audio_id,
                isShowInCharts: true,
            },
        ]);
        setDurationLabel("0:00");
        const audio = new Audio(topTrack.audio_url);
        const onMeta = () => {
            const total = audio.duration;
            if (Number.isFinite(total) && total > 0) {
                const m = Math.floor(total / 60);
                const s = Math.floor(total % 60);
                const dur = `${m}:${String(s).padStart(2, "0")}`;
                setDurationLabel(dur);
                setPlaylist([
                    {
                        id: topTrack.audio_id,
                        title: topTrack.song_title || noTags,
                        duration: dur,
                        date: formatDate(topTrack.song_created_at),
                        imageUrl: topTrack.cover_url || undefined,
                        audioUrl: topTrack.audio_url,
                        audioId: topTrack.audio_id,
                        isShowInCharts: true,
                    },
                ]);
            }
        };
        const onErr = () => setDurationLabel("0:00");
        audio.addEventListener("loadedmetadata", onMeta);
        audio.addEventListener("error", onErr);
        audio.load();
        return () => {
            audio.removeEventListener("loadedmetadata", onMeta);
            audio.removeEventListener("error", onErr);
        };
    }, [topTrack, language, noTags, setPlaylist]);
    const handlePlayTop = (entry) => {
        if (currentTrack?.audioId === entry.audio_id) {
            togglePlayPause();
            return;
        }
        openPlayer({
            id: entry.audio_id,
            title: entry.song_title || noTags,
            duration: durationLabel,
            date: formatDate(entry.song_created_at),
            imageUrl: entry.cover_url || undefined,
            audioUrl: entry.audio_url,
            audioId: entry.audio_id,
            isShowInCharts: true,
        });
    };
    const formatLikes = (count) => count.toLocaleString("ru-RU");
    const isTopPlaying = currentTrack?.audioId === topTrack?.audio_id && isPlaying;
    return (_jsxs(_Fragment, { children: [_jsx(Container, { size: "lg", className: `${styles.page} ${isPlayerOpen ? styles.containerPlayerOpen : ""}`, style: { padding: "16px 16px 100px" }, children: _jsxs("div", { className: styles.stack, children: [_jsxs("div", { className: styles.header, children: [_jsxs("div", { className: styles.titleWrap, children: [_jsx("span", { className: styles.eyebrow, children: "\u0412\u0418\u0420\u0422\u0423\u041E\u0417" }), _jsx("h1", { className: styles.pageTitle, children: language === "ru" ? "Главная" : "Home" })] }), _jsxs("div", { className: styles.headerActions, children: [_jsx(LanguageSwitch, { language: language, onSelect: handleSelectLanguage }), _jsx(ThemeSwitch, {}), isAuthenticated && !isTelegramMiniApp() && !isMaxMiniApp() && (_jsx("button", { type: "button", className: styles.logoutBtn, "aria-label": language === "ru" ? "Выйти" : "Log out", onClick: () => setLogoutConfirmOpened(true), children: _jsx(IconDoor, { size: 20 }) }))] })] }), !isLoadingTopTrack && topTrack && (_jsxs("div", { className: styles.songOfDayCard, children: [_jsx("div", { className: styles.songGlow }), _jsxs("div", { className: styles.songNotes, "aria-hidden": "true", children: [_jsx("span", { className: `${styles.note} ${styles.note1}`, children: "\u266B" }), _jsx("span", { className: `${styles.note} ${styles.note2}`, children: "\u266A" }), _jsx("span", { className: `${styles.note} ${styles.note3}`, children: "\u266C" }), _jsx("span", { className: `${styles.note} ${styles.note4}`, children: "\u266A" })] }), _jsxs("div", { className: styles.songContent, children: [_jsx("span", { className: styles.eyebrow, children: language === "ru" ? "ПЕСНЯ ДНЯ" : "SONG OF THE DAY" }), _jsx("span", { className: styles.songTitle, children: topTrack.song_title || noTags }), _jsxs("span", { className: styles.songLikes, children: [_jsx(IconHeart, { size: 14, fill: "var(--vp-brand)", color: "var(--vp-brand)" }), formatLikes(likeCount)] }), _jsxs("button", { type: "button", className: styles.listenBtn, onClick: () => handlePlayTop(topTrack), children: [isTopPlaying ? (_jsx(IconPlayerPause, { size: 16 })) : (_jsx(IconPlayerPlay, { size: 16 })), isTopPlaying
                                                    ? language === "ru"
                                                        ? "Пауза"
                                                        : "Pause"
                                                    : language === "ru"
                                                        ? "Слушать"
                                                        : "Listen"] })] })] })), _jsxs("button", { type: "button", className: styles.createSongBtn, onClick: handleCreateDemoSong, children: [_jsx(IconMusic, { size: 22, stroke: 2 }), _jsx("span", { className: styles.createSongLabel, children: language === "ru" ? "Создать песню" : "Create song" })] }), isAuthenticated && (_jsxs("div", { className: styles.balanceCard, children: [_jsxs("div", { className: styles.balanceLeft, children: [_jsx(IconWallet, { size: 20, color: "var(--vp-text-dim)" }), _jsxs("span", { className: styles.balanceLine, children: [t.balanceLabel, ": ", user?.balance ?? 0, " ", language === "ru" ? "песен" : "songs"] })] }), _jsxs("button", { type: "button", className: styles.topUpBtn, onClick: handleTopUp, children: [_jsx(IconPlus, { size: 16 }), t.topUp] })] })), !isAuthenticated && openLoginModal && (_jsxs("button", { type: "button", className: styles.guestLoginBtn, onClick: () => openLoginModal(), children: [_jsx(IconDoor, { size: 18 }), language === "ru" ? "Войти в аккаунт" : "Log in"] })), _jsxs("div", { className: styles.navCards, children: [_jsxs("button", { type: "button", className: styles.navCard, onClick: () => navigate("/my-songs"), children: [_jsx(IconMusic, { size: 22, className: styles.navCardIcon }), _jsx("span", { className: styles.navCardTitle, children: language === "ru" ? "Мои песни" : "My songs" }), songCount !== null && (_jsxs("span", { className: styles.navCardSub, children: [songCount, " ", language === "ru" ? "треков" : "tracks"] }))] }), _jsxs("button", { type: "button", className: styles.navCard, onClick: () => navigate("/charts"), children: [_jsx(IconChartBar, { size: 22, className: styles.navCardIcon }), _jsx("span", { className: styles.navCardTitle, children: language === "ru" ? "Чарты" : "Charts" })] }), (isAuthenticated || isGuest) && (_jsxs("button", { type: "button", className: styles.navCard, onClick: () => navigate("/referral"), children: [_jsx(IconGift, { size: 22, className: styles.navCardIcon }), _jsx("span", { className: styles.navCardTitle, children: t.invite })] }))] }), _jsxs("button", { type: "button", className: styles.telegramBtn, onClick: handleJoinCommunity, children: [_jsx(IconBrandTelegram, { size: 20 }), language === "ru"
                                    ? "Вступить в сообщество Telegram"
                                    : "Join Telegram community"] }), getMiniAppPlatform() === null && (_jsx(Menu, { className: styles.supportMenu, position: "top-start", minWidth: 230, trigger: _jsxs("button", { type: "button", className: styles.supportBtn, children: [_jsx(IconHelp, { size: 20 }), language === "ru" ? "Поддержка" : "Support"] }), items: [
                                {
                                    id: "telegram",
                                    icon: _jsx(IconBrandTelegram, { size: 16 }),
                                    label: language === "ru"
                                        ? "Написать нам в Telegram"
                                        : "Message us on Telegram",
                                    onClick: () => void openSupportBot("telegram"),
                                },
                                {
                                    id: "max",
                                    icon: _jsx(MaxMessengerBrandIcon, { size: 18 }),
                                    label: language === "ru" ? "Написать нам в MAX" : "Message us on MAX",
                                    onClick: () => void openSupportBot("max"),
                                },
                            ] }))] }) }), _jsx(LogoutConfirmModal, { opened: logoutConfirmOpened, onClose: () => setLogoutConfirmOpened(false), onConfirm: performLogout, language: language })] }));
};
export default MiniAppHomePage;
